import {
  SegmentRoot,
  SegmentTitle,
  SegmentImageCard,
  SegmentImage,
  SegmentInputSection,
  SegmentFullWidth,
  SegmentTileTile,
} from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/SegmentCommonStyles";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { SimpleCsticId } from "~/features/configurator/Linear/models";
import {
  chamfer,
  SHAFT_MACHINING_LEFT,
  SHAFT_MACHINING_RIGHT,
} from "~/features/configurator/Shaft/metric/metricCsticConstants";
import { selectCstic, selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import { useDispatch, useSelector } from "react-redux";
import { updateCsticValues } from "~/features/configurator/Linear/redux/actions";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { Button } from "~/common/mui";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { SimpleCsticTileField } from "~/features/configurator/Shaft/components/SimpleCsticTileField";
import { LinearTileV2 } from "~/features/configurator/Shaft/substeps/LinearTileV2";
import { AlarInput } from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/InchShaftRadialThreadSegment/InchShaftRadialThreadSegment";
import { METRIC_UNIT } from "~/features/configurator/Shaft/common/commonShaftConstants";
import {
  isChamferManualWidth,
  showChamferedSection,
  validateChamferSegment,
  type ValidationMode,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import type { RootState } from "~/app/store";
import {
  ErrorMessage,
  selectIsMachiningExtraSubstepInvalid,
} from "~/features/configurator/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstepHelper";
import { selectMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { selectConflictedCstics } from "~/features/configurator/Linear/redux/selectors";

import { KmatType } from "~/features/configurator/Linear/kmatType";
import { isNonNull } from "~/common/utils/reducers";

interface Props {
  side: "left" | "right";
}

const CONFIG = {
  left: {
    angle: {
      csticId: SimpleCsticId(chamfer.left.angle),
    },
    width: {
      value: SimpleCsticId(chamfer.left.width.sf),
      min: SimpleCsticId(chamfer.left.width.min),
      max: SimpleCsticId(chamfer.left.width.max),
      unit: METRIC_UNIT,
      label: "configurator.shaft.step.3.details.chamfer.width.title",
    },
    image: "configurator.shaft.step.3.details.chamfer.image.left",
    buttonLabel: "configurator.shaft.step.3.details.chamfer.copyToRight",
    otherSide: "right" as const,
    machining: SimpleCsticId(SHAFT_MACHINING_LEFT),
    if1Width: SimpleCsticId(chamfer.left.width.nf),
  },
  right: {
    angle: {
      csticId: SimpleCsticId(chamfer.right.angle),
    },
    width: {
      value: SimpleCsticId(chamfer.right.width.sf),
      min: SimpleCsticId(chamfer.right.width.min),
      max: SimpleCsticId(chamfer.right.width.max),
      unit: METRIC_UNIT,
      label: "configurator.shaft.step.3.details.chamfer.width.title",
    },
    image: "configurator.shaft.step.3.details.chamfer.image.right",
    // Hide copy to left button
    // buttonLabel: "configurator.shaft.step.3.details.chamfer.copyToLeft",
    otherSide: "left" as const,
    machining: SimpleCsticId(SHAFT_MACHINING_RIGHT),
    if1Width: SimpleCsticId(chamfer.right.width.nf),
  },
};

export const MetricShaftChamferSegment = (props: Props) => {
  const t = useXmlConfiguratorTranslations().t!;

  const config = CONFIG[props.side];
  const otherSideConfig = CONFIG[config.otherSide];

  const angleValue = useSelector(selectCstic(config.angle.csticId))?.value ?? "";
  const widthValue = useSelector(selectCstic(config.width.value))?.value ?? "";
  const if1WidthValue = useSelector(selectCstic(config.if1Width))?.value ?? "";
  const dispatch = useDispatch();

  const otherSideMachining = useSelector(selectCstic(otherSideConfig.machining))?.value ?? "";
  const showButton = showChamferedSection(otherSideMachining);

  const setOtherSide = () => {
    dispatch(
      updateCsticValues(
        [
          {
            ...otherSideConfig.angle.csticId,
            value: angleValue,
          },
          isChamferManualWidth(angleValue) && {
            ...otherSideConfig.width.value,
            value: widthValue,
          },
        ].filter(isNonNull),
        SubstepType.ShaftMachiningExtra,
      ),
    );
  };

  const errors = useSelector(
    selectErrors({
      angle: config.angle.csticId.key,
      width: {
        sf: config.width.value.key,
        nf: config.if1Width.key,
      },
    }),
  );
  const invalidAngle = !!errors.find((error) => error.cstic === config.angle.csticId.key);
  const invalidWidth = !!errors.find((error) => error.cstic === config.width.value.key);

  return (
    <SegmentRoot>
      <SegmentTitle>
        <LanguageAwareExternalContent
          content={"configurator.shaft.step.3.details.chamfer.title"}
          className="scha__lc__external-label"
        />
      </SegmentTitle>
      <SegmentImageCard>{<SegmentImage src={t(config.image)} alt="" />}</SegmentImageCard>
      <SegmentInputSection>
        {invalidAngle && <ErrorMessage />}
        <SegmentFullWidth>
          <SimpleCsticTileField
            {...config.angle}
            substepType={SubstepType.ShaftMachining}
            tilesPerRow={2}
            renderTile={({ onClick, selected, disabled, value, index }) => {
              if (!isSpecialAngle(value.value)) {
                return {
                  component: (
                    <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
                      <LinearTileV2.CheckedIndicator />
                      <LinearTileV2.StandardIndicator />
                      <SegmentTileTile>
                        <LanguageAwareExternalContent
                          content={NF_TRANSLATION_KEYS.find((x) => x.predicate(value.value, if1WidthValue))?.key}
                        />
                      </SegmentTileTile>
                    </LinearTileV2.Base>
                  ),
                  span: 2,
                };
              }

              return (
                <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
                  <LinearTileV2.CheckedIndicator />
                  <SegmentTileTile>{t("configurator.shaft.step.3.details.chamfer.custom.title")}</SegmentTileTile>
                  <p className="m-0">
                    {
                      <LanguageAwareExternalContent
                        content={SF_TRANSLATION_KEYS[value.value as keyof typeof SF_TRANSLATION_KEYS]}
                      />
                    }
                  </p>
                </LinearTileV2.Base>
              );
            }}
            csticsToSet={[
              (value) => {
                if (!isSpecialAngle(value)) {
                  return {
                    ...config.width.value,
                    value: null,
                  };
                }
              },
            ]}
          />
        </SegmentFullWidth>
        {isSpecialAngle(angleValue) && (
          <>
            {invalidWidth && <ErrorMessage variant={"input"} />}
            <AlarInput
              {...config.width}
              substepType={SubstepType.ShaftMachiningExtra}
              decimalScale={2}
              required
            />
          </>
        )}
        {"buttonLabel" in config && showButton && (
          <SegmentFullWidth>
            <Button onClick={setOtherSide} color={"secondary"} size={"medium"}>
              <LanguageAwareExternalContent content={config.buttonLabel} className={"scha__lc__external-label"} />
            </Button>
          </SegmentFullWidth>
        )}
      </SegmentInputSection>
    </SegmentRoot>
  );
};

const isSpecialAngle = (angle: string | null) => angle?.toUpperCase().startsWith("SF");

const SF_TRANSLATION_KEYS = {
  SF15: "configurator.shaft.step.3.details.chamfer.custom.value.15",
  SF20: "configurator.shaft.step.3.details.chamfer.custom.value.20",
  SF30: "configurator.shaft.step.3.details.chamfer.custom.value.30",
  SF45: "configurator.shaft.step.3.details.chamfer.custom.value.45",
} as const;

const NF_TRANSLATION_KEYS: {
  predicate: (angle: string | null, if1: string | null) => boolean | undefined;
  key: string;
}[] = [
  { predicate: (angle) => angle === "NF45", key: "configurator.shaft.step.3.details.chamfer.value.05" },
  {
    predicate: (angle, if1) => angle === "NF15" && if1?.startsWith("1,0"),
    key: "configurator.shaft.step.3.details.chamfer.value.10",
  },
  {
    predicate: (angle, if1) => angle === "NF15" && if1?.startsWith("1,5"),
    key: "configurator.shaft.step.3.details.chamfer.value.15",
  },
  {
    predicate: (angle, if1) => angle === "NF15" && if1?.startsWith("2,5"),
    key: "configurator.shaft.step.3.details.chamfer.value.25",
  },
];

// Get the 2nd param of isAxialThreadFullyConfigured as a type
type Config = Parameters<typeof validateChamferSegment>[0];
const selectErrors = (config: Config) => (state: RootState) => {
  const isSubstepInvalid = selectIsMachiningExtraSubstepInvalid(state);
  const { isDirty } = selectCsticExpanded(
    SimpleCsticId(config.width.sf, KmatType.Main),
    SubstepType.ShaftMachiningExtra,
  )(state);

  const mode: ValidationMode = isDirty || isSubstepInvalid ? "full" : "feedback";
  const kmat = selectMainKmat(state);
  const conflictedCstics = selectConflictedCstics(state);
  return validateChamferSegment(config, { kmat, conflictedCstics, mode });
};
